import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useTenant, useLanguage } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdRotator from '../../components/AdRotator';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import LanguageSelector from '../../components/LanguageSelector';
import { 
  Sun, Moon, Music, Calendar, Image, Video, MessageSquare, 
  Facebook, Instagram, Youtube, ExternalLink,
  MapPin, Clock, Ticket, ChevronRight, User, Menu, X, QrCode
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa6';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper pour résoudre les URLs d'images (relatives ou absolues)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

// Placeholder images
const PLACEHOLDER_PROFILE = '/images/artist-placeholder.jpg';
const PLACEHOLDER_COVER = 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1200&q=80';

// Hook pour gérer les erreurs d'images
const useImageWithFallback = (src, fallback) => {
  const [imgSrc, setImgSrc] = React.useState(src || fallback);
  const [hasError, setHasError] = React.useState(false);
  
  React.useEffect(() => {
    setImgSrc(src || fallback);
    setHasError(false);
  }, [src, fallback]);
  
  const onError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(fallback);
    }
  };
  
  return { imgSrc, onError };
};

// Icône X (Twitter) personnalisée
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Fonction robuste pour extraire l'ID YouTube
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Patterns YouTube possibles
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/ // ID direct
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// Générer l'URL embed YouTube sécurisée
const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
};

const Fanpage = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { tenant, fetchTenant, loading: tenantLoading } = useTenant();
  
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({
    senderName: '',
    senderEmail: '',
    subject: '',
    body: '',
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    if (artistSlug) {
      fetchTenant(artistSlug);
    }
  }, [artistSlug, fetchTenant]);

  useEffect(() => {
    if (user) {
      setMessageForm(prev => ({
        ...prev,
        senderName: `${user.firstName} ${user.lastName}`,
        senderEmail: user.email,
      }));
    }
  }, [user]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSendingMessage(true);
    
    try {
      await axios.post(`${API_URL}/messages?tenant_slug=${artistSlug}`, messageForm);
      toast.success('Message envoyé avec succès!');
      setMessageDialogOpen(false);
      setMessageForm(prev => ({ ...prev, subject: '', body: '' }));
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      FACEBOOK: <Facebook className="w-5 h-5" />,
      INSTAGRAM: <Instagram className="w-5 h-5" />,
      YOUTUBE: <Youtube className="w-5 h-5" />,
      TIKTOK: <FaTiktok className="w-5 h-5" />,
      X: <XIcon className="w-5 h-5" />,
      TWITTER: <XIcon className="w-5 h-5" />,
    };
    return icons[platform?.toUpperCase()] || <ExternalLink className="w-5 h-5" />;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price, currency = 'XOF') => {
    // Convert FCFA to XOF (ISO 4217 code)
    const currencyCode = currency === 'FCFA' ? 'XOF' : currency;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!tenant?.artist) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <h1 className="text-2xl font-bold">{t('language') === 'fr' ? 'Artiste non trouvé' : 'Artist not found'}</h1>
        <Button onClick={() => navigate('/')}>{t('language') === 'fr' ? "Retour à l'accueil" : 'Back to home'}</Button>
      </div>
    );
  }

  const { artist, socialLinks = [], events = [], posts = [], media = [] } = tenant;
  const photos = media.filter(m => m.type === 'PHOTO');
  const videos = media.filter(m => m.type === 'VIDEO' || m.type === 'YOUTUBE_LINK');
  const upcomingEvents = events.filter(e => new Date(e.dateTime) > new Date());
  const pastEvents = events.filter(e => new Date(e.dateTime) <= new Date());

  // URLs d'images avec fallback
  const profileImageUrl = resolveImageUrl(artist.profilePhotoUrl) || PLACEHOLDER_PROFILE;
  const coverImageUrl = resolveImageUrl(artist.coverPhotoUrl) || PLACEHOLDER_COVER;
  const logoImageUrl = resolveImageUrl(artist.logoUrl || artist.profilePhotoUrl) || PLACEHOLDER_PROFILE;
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (e, fallbackUrl) => {
    if (e.target.src !== fallbackUrl) {
      e.target.src = fallbackUrl;
    }
  };

  return (
    <div className="min-h-screen" style={{ '--tenant-primary': artist.primaryColor }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 py-1 flex items-center justify-between">
          <Link to={`/${artistSlug}`} className="flex items-center gap-3" data-testid="artist-logo">
            {artist.logoUrl || artist.profilePhotoUrl ? (
              <img 
                src={logoImageUrl} 
                alt={artist.nomDeScene}
                className="w-10 h-10 rounded-full object-cover border-2"
                style={{ borderColor: artist.primaryColor }}
                onError={(e) => handleImageError(e, PLACEHOLDER_PROFILE)}
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: artist.primaryColor }}
              >
                <Music className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-syne font-bold text-xl hidden sm:block">{artist.nomDeScene}</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => { setActiveSection('home'); document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }); }} 
              className={`text-sm hover:text-primary transition-colors ${activeSection === 'home' ? 'text-primary font-medium' : ''}`}
            >
              {t('home')}
            </button>
            <button 
              onClick={() => { setActiveSection('events'); document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' }); }} 
              className={`text-sm hover:text-primary transition-colors ${activeSection === 'events' ? 'text-primary font-medium' : ''}`}
            >
              {t('events')}
            </button>
            <button 
              onClick={() => { setActiveSection('media'); document.getElementById('media')?.scrollIntoView({ behavior: 'smooth' }); }} 
              className={`text-sm hover:text-primary transition-colors ${activeSection === 'media' ? 'text-primary font-medium' : ''}`}
            >
              {t('media')}
            </button>
            <button 
              onClick={() => { setActiveSection('news'); document.getElementById('news')?.scrollIntoView({ behavior: 'smooth' }); }} 
              className={`text-sm hover:text-primary transition-colors ${activeSection === 'news' ? 'text-primary font-medium' : ''}`}
            >
              {t('news')}
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {/* Scanner button - visible for ARTISTE, MANAGER, SCANNER */}
            {user && ['ARTISTE', 'MANAGER', 'SCANNER'].includes(user.role) && user.tenantId === tenant?.artist?.id && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(`/${artistSlug}/scanner`)}
                className="hidden md:flex items-center gap-2 border-primary/50 hover:bg-primary/10"
                data-testid="scanner-btn"
              >
                <QrCode className="w-4 h-4" />
                Scanner
              </Button>
            )}
            
            {user ? (
              <Button 
                size="sm" 
                onClick={() => navigate(user.role === 'FAN' ? `/${artistSlug}/fan/account` : `/${artistSlug}/management`)}
                style={{ backgroundColor: artist.primaryColor }}
                data-testid="account-btn"
              >
                <User className="w-4 h-4 mr-2" />
                {t('myAccount')}
              </Button>
            ) : (
              <Button 
                size="sm" 
                onClick={() => navigate(`/${artistSlug}/login`)}
                style={{ backgroundColor: artist.primaryColor }}
                data-testid="login-btn"
              >
                {t('login')}
              </Button>
            )}
            
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border px-4 py-4 space-y-3">
            <button onClick={() => { setActiveSection('home'); setMobileMenuOpen(false); document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left py-2 hover:text-primary">{t('home')}</button>
            <button onClick={() => { setActiveSection('events'); setMobileMenuOpen(false); document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left py-2 hover:text-primary">{t('events')}</button>
            <button onClick={() => { setActiveSection('media'); setMobileMenuOpen(false); document.getElementById('media')?.scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left py-2 hover:text-primary">{t('media')}</button>
            <button onClick={() => { setActiveSection('news'); setMobileMenuOpen(false); document.getElementById('news')?.scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left py-2 hover:text-primary">Actualités</button>
            
            {/* Scanner option - visible for ARTISTE, MANAGER, SCANNER */}
            {user && ['ARTISTE', 'MANAGER', 'SCANNER'].includes(user.role) && user.tenantId === tenant?.artist?.id && (
              <button 
                onClick={() => { setMobileMenuOpen(false); navigate(`/${artistSlug}/scanner`); }} 
                className="flex items-center gap-2 w-full text-left py-2 text-primary font-medium"
              >
                <QrCode className="w-5 h-5" />
                Scanner un billet
              </button>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section - Reduced height by 30% */}
      <section id="home" className="relative min-h-[42vh] flex items-end pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${coverImageUrl})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-end gap-6"
          >
            <img 
              src={profileImageUrl} 
              alt={artist.nomDeScene}
              className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-lg object-cover border-4 shadow-2xl flex-shrink-0"
              style={{ borderColor: artist.primaryColor }}
              data-testid="artist-profile-photo"
              onError={(e) => handleImageError(e, PLACEHOLDER_PROFILE)}
            />
            
            <div className="flex-1">
              <span className="accent-text" style={{ color: artist.primaryColor }}>{t('artist')}</span>
              <h1 className="font-syne text-4xl md:text-5xl lg:text-6xl font-extrabold mt-2">{artist.nomDeScene}</h1>
              <p className="text-lg text-muted-foreground mt-2">{artist.prenom} {artist.nom}</p>
              
              {/* Social Links */}
              <div className="flex items-center gap-3 mt-4">
                {socialLinks.map((link, index) => {
                  const platformId = (link.platform || '').toLowerCase();
                  return (
                    <a 
                      key={link.platform || index} 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                      style={{ color: artist.primaryColor }}
                      data-testid={`social-${platformId}`}
                    >
                      {getSocialIcon(link.platform)}
                    </a>
                  );
                })}
              </div>
            </div>
            
            <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="lg"
                  className="glow-gold"
                  style={{ backgroundColor: artist.primaryColor }}
                  data-testid="send-message-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('sendMessage')}
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card">
                <DialogHeader>
                  <DialogTitle>{t('sendMessage')} - {artist.nomDeScene}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendMessage} className="space-y-4 mt-4">
                  {!user && (
                    <>
                      <div className="space-y-2">
                        <Label>{t('firstName')}</Label>
                        <Input 
                          value={messageForm.senderName}
                          onChange={(e) => setMessageForm(prev => ({ ...prev, senderName: e.target.value }))}
                          required
                          data-testid="message-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Votre email</Label>
                        <Input 
                          type="email"
                          value={messageForm.senderEmail}
                          onChange={(e) => setMessageForm(prev => ({ ...prev, senderEmail: e.target.value }))}
                          required
                          data-testid="message-email"
                        />
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label>Sujet</Label>
                    <Input 
                      value={messageForm.subject}
                      onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                      required
                      data-testid="message-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea 
                      value={messageForm.body}
                      onChange={(e) => setMessageForm(prev => ({ ...prev, body: e.target.value }))}
                      rows={4}
                      required
                      data-testid="message-body"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full"
                    style={{ backgroundColor: artist.primaryColor }}
                    disabled={sendingMessage}
                    data-testid="message-submit"
                  >
                    {sendingMessage ? t('sending') : t('send')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </motion.div>
        </div>
      </section>

      {/* Ad Banner */}
      <AdRotator tenantSlug={artistSlug} />

      {/* Bio Section */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Card className="glass-card">
            <CardContent className="p-6 md:p-8">
              <h2 className="font-syne text-2xl font-bold mb-4" style={{ color: artist.primaryColor }}>{t('biography')}</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {artist.bio || `${artist.nomDeScene} - ${t('talentedArtist')}.`}
              </p>
              
              {(artist.emailContact || artist.telephoneContact) && (
                <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-6">
                  {artist.emailContact && (
                    <a href={`mailto:${artist.emailContact}`} className="text-sm hover:underline" style={{ color: artist.primaryColor }}>
                      {artist.emailContact}
                    </a>
                  )}
                  {artist.telephoneContact && (
                    <a href={`tel:${artist.telephoneContact}`} className="text-sm hover:underline" style={{ color: artist.primaryColor }}>
                      {artist.telephoneContact}
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-12 px-4 sm:px-6 bg-secondary/30" id="events">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="accent-text" style={{ color: artist.primaryColor }}>{t('upcoming')}</span>
                <h2 className="font-syne text-3xl font-bold mt-1">{t('events')}</h2>
              </div>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-card card-hover overflow-hidden h-full" data-testid={`event-card-${event.id}`}>
                    <div className="relative h-48">
                      <img 
                        src={event.coverImageUrl || '/images/event-placeholder.jpg'} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: artist.primaryColor, color: '#fff' }}>
                        {formatPrice(event.ticketPrice, event.currency)}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-syne font-bold text-xl mb-3">{event.title}</h3>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" style={{ color: artist.primaryColor }} />
                          <span>{formatDate(event.dateTime)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{ color: artist.primaryColor }} />
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>
                      <Button 
                        className="w-full mt-4"
                        style={{ backgroundColor: artist.primaryColor }}
                        onClick={() => navigate(`/${artistSlug}/event/${event.id}`)}
                        data-testid={`buy-ticket-${event.id}`}
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        {t('buyATicket')}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className={`py-12 px-4 sm:px-6 ${upcomingEvents.length === 0 ? 'bg-secondary/30' : ''}`} id={upcomingEvents.length === 0 ? 'events' : 'past-events'}>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="accent-text" style={{ color: artist.primaryColor }}>{t('events')}</span>
                <h2 className="font-syne text-3xl font-bold mt-1">{upcomingEvents.length === 0 ? t('events') : t('pastEvents')}</h2>
              </div>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {pastEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-card card-hover overflow-hidden h-full opacity-90" data-testid={`past-event-card-${event.id}`}>
                    <div className="relative h-48">
                      <img 
                        src={event.coverImageUrl || '/images/event-placeholder.jpg'} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium bg-black/60 text-white">
                        {t('ended')}
                      </div>
                      <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: artist.primaryColor, color: '#fff' }}>
                        {formatPrice(event.ticketPrice, event.currency)}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-syne font-bold text-xl mb-3">{event.title}</h3>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" style={{ color: artist.primaryColor }} />
                          <span>{formatDate(event.dateTime)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{ color: artist.primaryColor }} />
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Media Gallery */}
      {(photos.length > 0 || videos.length > 0) && (
        <section className="py-12 px-4 sm:px-6" id="media">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <span className="accent-text" style={{ color: artist.primaryColor }}>{t('gallery')}</span>
              <h2 className="font-syne text-3xl font-bold mt-1">{t('media')}</h2>
            </div>
            
            {/* Photos */}
            {photos.length > 0 && (
              <div className="mb-12">
                <h3 className="font-syne font-bold text-xl mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5" style={{ color: artist.primaryColor }} />
                  Photos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      whileHover={{ scale: 1.02 }}
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer"
                    >
                      <img 
                        src={photo.url} 
                        alt={photo.title || 'Photo'}
                        className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Videos */}
            {videos.length > 0 && (
              <div>
                <h3 className="font-syne font-bold text-xl mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5" style={{ color: artist.primaryColor }} />
                  Vidéos
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => {
                    const embedUrl = video.type === 'YOUTUBE_LINK' ? getYouTubeEmbedUrl(video.url) : null;
                    return (
                    <Card key={video.id} className="glass-card overflow-hidden">
                      <div className="aspect-video bg-black">
                        {video.type === 'YOUTUBE_LINK' && embedUrl ? (
                          <iframe
                            src={embedUrl}
                            title={video.title || 'Vidéo YouTube'}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="strict-origin-when-cross-origin"
                          />
                        ) : video.type === 'YOUTUBE_LINK' ? (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <p>Lien YouTube invalide</p>
                          </div>
                        ) : (
                          <video src={video.url} controls className="w-full h-full object-cover" />
                        )}
                      </div>
                      {video.title && (
                        <CardContent className="p-4">
                          <p className="font-medium">{video.title}</p>
                        </CardContent>
                      )}
                    </Card>
                  )})}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* News/Posts */}
      {posts.length > 0 && (
        <section className="py-12 px-4 sm:px-6 bg-secondary/30" id="news">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <span className="accent-text" style={{ color: artist.primaryColor }}>{t('news')}</span>
              <h2 className="font-syne text-3xl font-bold mt-1">{t('latestNews')}</h2>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-card card-hover h-full">
                    {post.coverImageUrl && (
                      <div className="h-48 overflow-hidden">
                        <img src={post.coverImageUrl} alt={post.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <p className="text-xs text-muted-foreground mb-2">
                        {new Date(post.publishedAt).toLocaleDateString('fr-FR')}
                      </p>
                      <h3 className="font-syne font-bold text-xl mb-3">{post.title}</h3>
                      <p className="text-muted-foreground line-clamp-3">{post.content}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <SenArtisteLogo className="w-5 h-5" />
            <span className="font-syne font-bold">{artist.nomDeScene}</span>
            <span className="text-muted-foreground text-sm">{t('onSenArtiste')}</span>
          </div>
          
          <div className="flex items-center gap-4">
            {socialLinks.map((link, index) => (
              <a 
                key={link.platform || index} 
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                data-testid={`footer-social-${link.platform.toLowerCase()}`}
              >
                {getSocialIcon(link.platform)}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Fanpage;
